<template>
  <div class="pending-documents-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Oczekujące dokumenty</h1>
      </div>
    </div>
    <v-row>
      <v-col cols="12">
        <v-card class="content-card">
          <v-card-title class="text-h5 primary--text font-weight-bold">
            <v-icon left color="primary">mdi-file-clock</v-icon>
            {{ $t('pendingDocuments.title') }}
          </v-card-title>

          <v-card-text>

            <!-- Documents Table -->
            <v-data-table
              :headers="headers"
              :items="documents"
              :loading="loading"
              :options.sync="options"
              :server-items-length="totalItems"
              :footer-props="{
                'items-per-page-options': [10, 25, 50, 100],
                'items-per-page-text': $t('pendingDocuments.docsPerPage'),
                'page-text': '{0}-{1} ' + $t('pendingDocuments.of') + ' {2}'
              }"
              class="elevation-1"
              :loading-text="$t('pendingDocuments.loadingDocs')"
              :no-data-text="$t('pendingDocuments.noDocsToShow')"
            >
              <!-- Driver Info -->
              <template v-slot:[`item.driver`]="{ item }">
                <div class="d-flex align-center">
                  <v-avatar size="40" class="mr-3">
                    <v-icon>mdi-account</v-icon>
                  </v-avatar>
                  <div>
                    <div class="font-weight-medium">
                      {{ item.driver.firstName }} {{ item.driver.lastName }}
                    </div>
                    <div class="text-caption grey--text">
                      ID: {{ item.driver.id }}
                    </div>
                  </div>
                </div>
              </template>

              <!-- Document Template -->
              <template v-slot:[`item.template`]="{ item }">
                <div>
                  <div class="font-weight-medium">{{ item.documentType }}</div>
                  <div class="text-caption grey--text">ID: {{ item.templateId }}</div>
                </div>
              </template>

              <!-- Status -->
              <template v-slot:[`item.status`]="{ item }">
                <v-chip
                  small
                  :color="getStatusColor(item)"
                  text-color="white"
                >
                  {{ getStatusText(item) }}
                </v-chip>
                <div v-if="item.signature" class="text-caption grey--text mt-1">
                  <v-icon small color="green">mdi-draw</v-icon> {{ $t('pendingDocuments.signed') }}
                </div>
              </template>

              <!-- Required Badge -->
              <template v-slot:[`item.isRequiredForDriver`]="{ item }">
                <v-chip
                  v-if="item.isRequiredForDriver"
                  x-small
                  color="red"
                  text-color="white"
                >
                  {{ $t('pendingDocuments.requiredBadge') }}
                </v-chip>
                <span v-else class="text-caption grey--text">{{ $t('pendingDocuments.optional') }}</span>
              </template>

              <!-- Created Date -->
              <template v-slot:[`item.createdAt`]="{ item }">
                <div>
                  <div>{{ formatDate(item.createdAt) }}</div>
                  <div class="text-caption grey--text">
                    {{ formatTimeAgo(item.createdAt) }}
                  </div>
                </div>
              </template>

              <!-- Actions -->
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  small
                  color="primary"
                  @click="redirectToDriver(item.driver.id)"
                  class="mr-2"
                >
                  <v-icon left small>mdi-account-details</v-icon>
                  {{ $t('common.driver') }}
                </v-btn>

                <v-btn
                  small
                  color="info"
                  outlined
                  @click="previewDocument(item)"
                  class="mr-2"
                >
                  <v-icon left small>mdi-eye</v-icon>
                  {{ $t('common.preview') }}
                </v-btn>

                <v-btn
                  v-if="item.source === 'onboarding' || item.data || (item.files && item.files.length > 0)"
                  small
                  color="info"
                  @click="downloadDocument(item)"
                  :loading="downloadingDocuments.includes(item.id)"
                  class="mr-2"
                >
                  <v-icon left small>mdi-download</v-icon>
                  {{ $t('common.download') }}
                </v-btn>

                <v-btn
                  v-if="item.signature"
                  small
                  color="purple"
                  @click="viewSignature(item)"
                  class="mr-2"
                >
                  <v-icon left small>mdi-draw</v-icon>
                  {{ $t('pendingDocuments.signature') }}
                </v-btn>

                <v-btn
                  v-if="!item.accepted && !item.deleted"
                  small
                  color="success"
                  @click="acceptDocument(item)"
                  :loading="processingDocuments.includes(item.id)"
                  class="mr-2"
                >
                  <v-icon left small>mdi-check</v-icon>
                  {{ $t('common.approve') }}
                </v-btn>

                <v-btn
                  v-if="!item.deleted"
                  small
                  color="warning"
                  @click="rejectDocument(item)"
                  :loading="processingDocuments.includes(item.id)"
                >
                  <v-icon left small>mdi-close-circle</v-icon>
                  {{ $t('common.reject') }}
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Bulk Actions FAB -->
    <v-speed-dial
      v-model="fab"
      bottom
      right
      direction="top"
      open-on-hover
    >
      <template v-slot:activator>
        <v-btn
          v-model="fab"
          color="primary"
          dark
          fab
        >
          <v-icon v-if="fab">mdi-close</v-icon>
          <v-icon v-else>mdi-cog</v-icon>
        </v-btn>
      </template>

      <v-btn
        fab
        dark
        small
        color="success"
        @click="acceptAllPending"
        :disabled="!hasPendingDocuments"
      >
        <v-icon>mdi-check-all</v-icon>
      </v-btn>

      <v-btn
        fab
        dark
        small
        color="info"
        @click="exportDocuments"
      >
        <v-icon>mdi-export</v-icon>
      </v-btn>
    </v-speed-dial>

    <!-- Confirmation Dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="confirmDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">
          {{ confirmAction.title }}
        </v-card-title>
        <v-card-text>
          {{ confirmAction.text }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmDialog = false">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            :color="confirmAction.color"
            @click="executeConfirmedAction"
            :loading="confirmAction.loading"
          >
            {{ confirmAction.confirmText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Signature Dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="signatureDialog" max-width="700">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left>mdi-draw</v-icon>
          {{ $t('pendingDocuments.documentSignature') }}
        </v-card-title>
        <v-card-text v-if="selectedDocument">
          <div class="text-center">
            <div class="mb-3">
              <strong>{{ $t('common.driver') }}:</strong> {{ selectedDocument.driver.firstName }} {{ selectedDocument.driver.lastName }}
            </div>
            <div class="mb-3">
              <strong>{{ $t('pendingDocuments.document') }}:</strong> {{ selectedDocument.documentType }}
            </div>
            <div class="mb-3">
              <strong>{{ $t('pendingDocuments.signatureDate') }}:</strong> {{ formatDate(selectedDocument.updatedAt) }}
            </div>
            <div class="signature-container">
              <img
                :src="selectedDocument.signature"
                :alt="$t('pendingDocuments.driverSignature')"
                class="signature-image"
              />
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="signatureDialog = false">
            {{ $t('common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Preview Dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="previewDialogOpen" max-width="900" scrollable>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-eye</v-icon>
          {{ $t('pendingDocuments.documentPreview') }}
          <v-spacer />
          <v-btn icon @click="previewDialogOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="background: #fafafa;">
          <div v-if="previewMime === 'text/html'" v-html="previewHtml" class="pa-4"></div>
          <iframe v-else-if="previewMime === 'application/pdf' && previewUrl" :src="previewUrl" style="width: 100%; height: 70vh; border: none;"></iframe>
          <img v-else-if="previewUrl" :src="previewUrl" style="max-width: 100%; max-height: 70vh; display: block; margin: 0 auto;" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  name: 'PendingDocuments',
  data() {
    return {
      loading: false,
      documents: [],
      totalItems: 0,
      search: '',
      searchTimeout: null,
      statusFilter: null,
      sortBy: 'createdAt_desc',

      options: {
        page: 1,
        itemsPerPage: 25,
      },

      headers: [
        {
          text: 'Kierowca',
          value: 'driver',
          sortable: false,
          width: '200px'
        },
        {
          text: 'Dokument',
          value: 'template',
          sortable: false,
          width: '250px'
        },
        {
          text: 'Status',
          value: 'status',
          sortable: false,
          width: '120px'
        },
        {
          text: 'Priorytet',
          value: 'isRequiredForDriver',
          sortable: false,
          width: '100px'
        },
        {
          text: 'Data utworzenia',
          value: 'createdAt',
          sortable: true,
          width: '180px'
        },
        {
          text: 'Akcje',
          value: 'actions',
          sortable: false,
          width: '300px'
        }
      ],

      statusOptions: [
        { text: 'Wszystkie', value: null },
        { text: 'Oczekujące', value: 'pending' },
        { text: 'Wysłane', value: 'submitted' },
        { text: 'Zaakceptowane', value: 'accepted' },
        { text: 'Usunięte', value: 'deleted' }
      ],

      sortOptions: [
        { text: 'Data utworzenia (najnowsze)', value: 'createdAt_desc' },
        { text: 'Data utworzenia (najstarsze)', value: 'createdAt_asc' },
        { text: 'Nazwisko kierowcy A-Z', value: 'driver_lastName_asc' },
        { text: 'Nazwisko kierowcy Z-A', value: 'driver_lastName_desc' },
        { text: 'Nazwa dokumentu A-Z', value: 'template_name_asc' },
        { text: 'Wymagane najpierw', value: 'isRequiredForDriver_desc' }
      ],

      processingDocuments: [],
      downloadingDocuments: [],

      fab: false,
      confirmDialog: false,
      confirmAction: {
        title: '',
        text: '',
        color: '',
        confirmText: '',
        loading: false,
        action: null,
        item: null
      },
      signatureDialog: false,
      previewDialogOpen: false,
      previewHtml: '',
      previewUrl: null,
      previewMime: '',
      selectedDocument: null
    };
  },

  computed: {
    hasPendingDocuments() {
      return this.documents.some(doc => !doc.accepted && !doc.deleted && !doc.submitted);
    }
  },

  watch: {
    options: {
      handler() {
        this.fetchDocuments();
      },
      deep: true
    }
  },

  methods: {
    async fetchDocuments() {
      this.loading = true;
      try {
        const params = {
          page: this.options.page,
          itemsPerPage: this.options.itemsPerPage,
          search: this.search || null,
          status: this.statusFilter || null,
          sortBy: this.sortBy || 'createdAt_desc'
        };

        const response = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'documents/pending-documents',
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token')
          },
          params
        });

        this.documents = response.data?.data?.result || response.data || [];
        this.totalItems = response.data?.data?.count || response.data?.length || 0;

      } catch (error) {
        console.error('Error fetching documents:', error);
        Swal.fire({
          title: 'Błąd!',
          text: 'Nie udało się pobrać dokumentów',
          icon: 'error'
        });
      } finally {
        this.loading = false;
      }
    },

    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.options.page = 1; // Reset to first page
        this.fetchDocuments();
      }, 500);
    },

    refreshDocuments() {
      this.fetchDocuments();
    },

    redirectToDriver(driverId) {
      this.$router.push({ path: '/driver-details', query: { id: driverId } });
    },

    async downloadDocument(document) {
      this.downloadingDocuments.push(document.id);
      try {
        if (document.source === 'onboarding') {
          await this.downloadOnboardingDocument(document);
        } else if (document.data) {
          await this.downloadDocumentData(document.id);
        } else if (document.files && document.files.length > 0) {
          await this.downloadTemplate(document.files[0].id);
        } else {
          Swal.fire({ title: 'Brak plików', text: 'Ten dokument nie zawiera plików do pobrania', icon: 'warning' });
          return;
        }
      } catch (error) {
        console.error('Error downloading document:', error);
        Swal.fire({ title: 'Błąd!', text: 'Wystąpił błąd podczas pobierania dokumentu', icon: 'error' });
      } finally {
        this.downloadingDocuments = this.downloadingDocuments.filter(id => id !== document.id);
      }
    },

    onboardingMimeFor(name) {
      const ext = (name || '').split('.').pop().toLowerCase();
      return ({ pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp' })[ext] || 'application/octet-stream';
    },
    base64ToBlob(b64, mime) {
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return new Blob([bytes], { type: mime });
    },
    async fetchOnboardingFile(doc) {
      const response = await axios({
        method: 'get',
        url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/document/file/' + doc.id,
        headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
      });
      const payload = response.data?.data || response.data || {};
      if (!payload.fileData) throw new Error('Brak danych pliku');
      const mime = this.onboardingMimeFor(payload.originalName || payload.fileUrl);
      const blob = this.base64ToBlob(payload.fileData, mime);
      return { blob, mime, name: payload.originalName || 'dokument' };
    },
    async downloadOnboardingDocument(doc) {
      const { blob, name } = await this.fetchOnboardingFile(doc);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    },
    async previewDocument(doc) {
      try {
        if (doc.source === 'onboarding') {
          const { blob, mime } = await this.fetchOnboardingFile(doc);
          const url = window.URL.createObjectURL(blob);
          this.previewMime = mime;
          this.previewUrl = url;
          this.previewDialogOpen = true;
        } else {
          // Podgląd wypełnionego dokumentu jako PDF (DOCX→PDF na backendzie,
          // jak podgląd faktury) — zamiast sklejanego HTML z XML-a (krzaczki).
          const res = await axios({
            method: 'get',
            url: process.env.VUE_APP_ROOT_API + 'documents/preview-pdf/' + doc.id,
            headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
            responseType: 'blob',
          });
          const blob = new Blob([res.data], { type: 'application/pdf' });
          this.previewUrl = window.URL.createObjectURL(blob);
          this.previewMime = 'application/pdf';
          this.previewDialogOpen = true;
        }
      } catch (e) {
        const msg = e?.response?.data?.message?.toString?.() || e?.message || 'Nie udało się otworzyć podglądu';
        Swal.fire('Błąd', msg, 'error');
      }
    },

    async downloadTemplate(fileId) {
      const response = await axios({
        method: 'post',
        url: process.env.VUE_APP_ROOT_API + 'aws/download-file/' + fileId,
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('token')
        },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: response.data.type });
      const url = window.URL.createObjectURL(blob);
      window.open(url);
    },

    acceptDocument(document) {
      this.confirmAction = {
        title: 'Akceptuj dokument',
        text: `Czy na pewno chcesz zaakceptować dokument "${document.documentType}" od kierowcy ${document.driver.firstName} ${document.driver.lastName}?`,
        color: 'success',
        confirmText: 'Akceptuj',
        loading: false,
        action: 'accept',
        item: document
      };
      this.confirmDialog = true;
    },

    async rejectDocument(document) {
      const res = await Swal.fire({
        title: 'Odrzucić dokument?',
        html: `<b>${document.documentType}</b> od kierowcy <b>${document.driver.firstName} ${document.driver.lastName}</b><br>Podaj powód odrzucenia — kierowca zobaczy ten komentarz.`,
        icon: 'warning',
        input: 'textarea',
        inputPlaceholder: 'np. Zdjęcie jest nieczytelne, prześlij wyraźniejsze...',
        inputAttributes: { 'aria-label': 'Powód odrzucenia' },
        inputValidator: (v) => (!v || v.trim().length < 3 ? 'Podaj powód (min. 3 znaki)' : null),
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Odrzuć',
        cancelButtonText: 'Anuluj',
      });
      if (!res.isConfirmed) return;
      const reason = (res.value || '').trim();

      this.processingDocuments.push(document.id);
      try {
        const isOnboarding = document.source === 'onboarding';
        await axios({
          method: isOnboarding ? 'post' : 'patch',
          url: process.env.VUE_APP_ROOT_API + (isOnboarding
            ? 'driver-registration/admin/document/reject'
            : 'documents/reject'),
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { documentId: document.id, reason },
        });
        Swal.fire({ title: 'Odrzucono', text: 'Kierowca otrzyma powiadomienie', icon: 'success', timer: 1800 });
        this.fetchDocuments();
      } catch (e) {
        const msg = e?.response?.data?.message?.toString?.() || e?.message || 'Błąd odrzucania dokumentu';
        Swal.fire('Błąd', msg, 'error');
      }
      this.processingDocuments = this.processingDocuments.filter((id) => id !== document.id);
    },

    async executeConfirmedAction() {
      this.confirmAction.loading = true;

      try {
        if (this.confirmAction.action === 'accept') {
          await this.performAcceptDocument(this.confirmAction.item);
        } else if (this.confirmAction.action === 'delete') {
          await this.performDeleteDocument(this.confirmAction.item);
        }

        this.confirmDialog = false;
        this.fetchDocuments(); // Refresh the list

      } catch (error) {
        console.error('Error executing action:', error);
        Swal.fire({
          title: 'Błąd!',
          text: 'Wystąpił błąd podczas wykonywania operacji',
          icon: 'error'
        });
      } finally {
        this.confirmAction.loading = false;
      }
    },

    async performAcceptDocument(document) {
      this.processingDocuments.push(document.id);

      try {
        const isOnboarding = document.source === 'onboarding';
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + (isOnboarding
            ? 'driver-registration/admin/document/verify'
            : 'documents/accept'),
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token')
          },
          data: {
            documentId: document.id
          }
        });

        Swal.fire({
          title: 'Sukces!',
          text: 'Dokument został zaakceptowany',
          icon: 'success',
          timer: 2000
        });

      } finally {
        this.processingDocuments = this.processingDocuments.filter(id => id !== document.id);
      }
    },

    async performDeleteDocument(document) {
      this.processingDocuments.push(document.id);

      try {
        const isOnboarding = document.source === 'onboarding';
        if (isOnboarding) {
          // Dla onboarding — "usunięcie" = odrzucenie z powodem technicznym
          await axios({
            method: 'post',
            url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/document/reject',
            headers: {
              Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
            data: { documentId: document.id, reason: 'Usunięto przez administratora' },
          });
        } else {
          await axios({
            method: 'delete',
            url: process.env.VUE_APP_ROOT_API + 'documents/delete/' + document.id,
            headers: {
              Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
          });
        }

        Swal.fire({
          title: 'Usunięto!',
          text: 'Dokument został usunięty',
          icon: 'success',
          timer: 2000
        });

      } finally {
        this.processingDocuments = this.processingDocuments.filter(id => id !== document.id);
      }
    },

    async acceptAllPending() {
      const result = await Swal.fire({
        title: 'Zaakceptuj wszystkie oczekujące',
        text: 'Czy na pewno chcesz zaakceptować wszystkie oczekujące dokumenty?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Tak, akceptuj wszystkie',
        cancelButtonText: 'Anuluj'
      });

      if (result.isConfirmed) {
        const pendingDocs = this.documents.filter(doc => !doc.accepted && !doc.deleted && !doc.submitted);

        for (const doc of pendingDocs) {
          try {
            await this.performAcceptDocument(doc);
          } catch (error) {
            console.error('Error accepting document:', doc.id, error);
          }
        }

        this.fetchDocuments();
      }
    },

    async exportDocuments() {
      try {
        const response = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'documents/export-pending',
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token')
          },
          responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `oczekujace-dokumenty-${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        Swal.fire({
          title: 'Sukces!',
          text: 'Eksport został rozpoczęty',
          icon: 'success',
          timer: 2000
        });
      } catch (error) {
        console.error('Error exporting documents:', error);
        Swal.fire({
          title: 'Błąd!',
          text: 'Wystąpił błąd podczas eksportu',
          icon: 'error'
        });
      }
    },

    getStatusColor(document) {
      if (document.deleted) return 'red';
      if (document.accepted) return 'green';
      if (document.submitted) return 'blue';
      return 'orange';
    },

    getStatusText(document) {
      if (document.deleted) return 'Usunięty';
      if (document.accepted) return 'Zaakceptowany';
      if (document.submitted) return 'Wysłany';
      return 'Oczekujący';
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatTimeAgo(dateString) {
      const now = new Date();
      const date = new Date(dateString);
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 1) return 'wczoraj';
      if (diffDays < 7) return `${diffDays} dni temu`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} tygodni temu`;
      return `${Math.floor(diffDays / 30)} miesięcy temu`;
    },

    async downloadDocumentData(documentId) {
      const response = await axios({
        method: 'get',
        url: process.env.VUE_APP_ROOT_API + 'documents/download/' + documentId,
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('token')
        },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'dokument.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    },

    viewSignature(document) {
      if (!document.signature) {
        Swal.fire({
          title: 'Brak podpisu',
          text: 'Ten dokument nie zawiera podpisu',
          icon: 'warning'
        });
        return;
      }

      this.selectedDocument = document;
      this.signatureDialog = true;
    }
  },

  mounted() {
    this.fetchDocuments();
  }
};
</script>

<style scoped>
.pending-documents-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5478FF;
  font-weight: 600;
}
.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0 0;
  color: #FFFFFF;
}
.content-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-radius: 12px !important;
  border: 1px solid #2A3142 !important;
}
.pending-documents-page >>> .v-data-table {
  background: transparent !important;
  color: #E6EAF2 !important;
}
.pending-documents-page >>> .v-data-table thead tr th {
  background: #1F2433 !important;
  color: #94A0B8 !important;
  border-bottom: 1px solid #2A3142 !important;
}
.pending-documents-page >>> .v-data-table tbody tr td {
  border-bottom: 1px solid #2A3142 !important;
  color: #E6EAF2 !important;
}
.pending-documents-page >>> .v-data-table tbody tr:hover {
  background: rgba(84, 120, 255, 0.06) !important;
}
.pending-documents-page >>> .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.pending-documents-page >>> .v-text-field input,
.pending-documents-page >>> .v-text-field textarea,
.pending-documents-page >>> .v-select__selection {
  color: #E6EAF2 !important;
}
.pending-documents-page >>> .v-label {
  color: #94A0B8 !important;
}
.pending-documents-page >>> .grey--text {
  color: #94A0B8 !important;
}
.pending-documents-page >>> .primary--text {
  color: #5478FF !important;
}
.pending-documents-page >>> .v-card__title {
  color: #E6EAF2 !important;
}
.v-data-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.v-speed-dial {
  position: fixed;
}

.font-weight-medium {
  font-weight: 500 !important;
}

.signature-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.signature-image {
  max-width: 100%;
  max-height: 300px;
  border: 2px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
</style>